import { useState, createElement, startTransition } from 'react';
import { DragStartEvent, DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { FolderContent } from '@/api/generatedApi';
import { useDndSensors } from '@/components/dnd';
import { getContentParentFolderId, isFolder } from '@/utils/folders';
import { useContentDragDispatch } from './ContentDragContext';
import { ContentDragPreview } from './ContentDragPreview';

interface UseFolderDndOptions {
  onMoveContents: (contents: FolderContent[], targetFolderId: string, targetFolderName?: string) => void;
  onDropOnHiddenFolders?: (contents: FolderContent[]) => void;
}

export const useFolderDnd = ({ onMoveContents, onDropOnHiddenFolders }: UseFolderDndOptions) => {
  const { currentFolderId, setDraggedContents, setDropTargetFolderId } = useContentDragDispatch();
  const [draggedContentsData, setDraggedContentsData] = useState<FolderContent[]>([]);

  const sensors = useDndSensors();

  const handleDragStart = (event: DragStartEvent) => {
    const dragData = event.active.data.current as { contents?: FolderContent[] } | undefined;
    if (dragData?.contents && dragData.contents.length > 0) {
      setDraggedContentsData(dragData.contents);
      startTransition(() => setDraggedContents(dragData.contents ?? []));
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    const dropData = over?.data.current as { type: string; folderId?: string } | undefined;

    let nextId: string | undefined;
    switch (dropData?.type) {
      case 'folder-drop-target': {
        nextId = dropData.folderId;
        break;
      }
      case 'root-drop-target': {
        nextId = 'root';
        break;
      }
      case 'hidden-folders-drop-target': {
        nextId = 'hidden-folders';
        break;
      }
      default: {
        nextId = undefined;
      }
    }

    setDropTargetFolderId(previous => (nextId === previous ? previous : nextId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const dragData = active.data.current as { contents?: FolderContent[] } | undefined;
    const dropData = over?.data.current as { type: string; folderId?: string; folderName?: string } | undefined;

    setDraggedContentsData([]);
    startTransition(() => {
      setDraggedContents([]);
      setDropTargetFolderId(undefined);
    });

    if (!over) return;

    if (dropData?.type === 'hidden-folders-drop-target') {
      if (dragData?.contents && dragData.contents.length > 0 && onDropOnHiddenFolders) {
        onDropOnHiddenFolders(dragData.contents);
      }
      return;
    }

    let targetFolderId: string;
    let targetFolderName: string | undefined;
    if (dropData?.type === 'root-drop-target') {
      targetFolderId = 'root';
    } else if (dropData?.type === 'folder-drop-target' && dropData.folderId) {
      targetFolderId = dropData.folderId;
      targetFolderName = dropData.folderName;
    } else {
      return;
    }

    if (dragData?.contents && dragData.contents.length > 0) {
      const contentsToMove = dragData.contents.filter(content => {
        const parentId = getContentParentFolderId(content);
        if (isFolder(content) && targetFolderId === content.id) return false;
        if (targetFolderId === parentId) return false;
        if (targetFolderId === 'root' && parentId === undefined) return false;
        return true;
      });

      if (contentsToMove.length > 0) {
        onMoveContents(contentsToMove, targetFolderId, targetFolderName);
      }
    }
  };

  const handleDragCancel = () => {
    setDraggedContentsData([]);
    startTransition(() => {
      setDraggedContents([]);
      setDropTargetFolderId(undefined);
    });
  };

  const dragOverlay =
    draggedContentsData.length > 0 ? createElement(ContentDragPreview, { contents: draggedContentsData }) : undefined;

  return {
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    dragOverlay,
    isDragging: draggedContentsData.length > 0,
    currentFolderId,
  };
};
